import { supabase } from '@/integrations/supabase/client';
import type { RunSummary, FlawSummary } from '../types/history';

export class RunHistoryService {
  async getUserRunHistory(page = 0, limit = 10): Promise<{ runs: RunSummary[]; total: number }> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    // Get total count first
    const { count, error: countError } = await supabase
      .from('swing_analysis_runs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('status', 'completed')
      .not('ai_meta', 'is', null);

    if (countError) throw new Error(`Failed to get run count: ${countError.message}`);

    // Get paginated runs
    const { data, error } = await supabase
      .from('swing_analysis_runs')
      .select('id, created_at, video_asset_id, ai_meta, status')
      .eq('user_id', user.id)
      .eq('status', 'completed')
      .not('ai_meta', 'is', null)
      .order('created_at', { ascending: false })
      .range(page * limit, (page + 1) * limit - 1);

    if (error) throw new Error(`Failed to get run history: ${error.message}`);

    const runs = data.map(this.mapToRunSummary);
    return { runs, total: count || 0 };
  }

  async getRunById(runId: string): Promise<RunSummary | null> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('swing_analysis_runs')
      .select('id, created_at, video_asset_id, ai_meta, status')
      .eq('id', runId)
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to get run: ${error.message}`);
    }

    return this.mapToRunSummary(data);
  }

  private mapToRunSummary(data: any): RunSummary {
    const aiMeta = data.ai_meta || {};
    const videoContext = aiMeta.video_context || {};
    const flaws = aiMeta.flaws || [];

    // Get top 3 flaws by severity and confidence
    const primaryFlaws: FlawSummary[] = flaws
      .sort((a: any, b: any) => {
        const severityOrder = { critical: 3, moderate: 2, minor: 1 };
        const aSeverity = severityOrder[a.severity as keyof typeof severityOrder] || 0;
        const bSeverity = severityOrder[b.severity as keyof typeof severityOrder] || 0;
        
        if (aSeverity !== bSeverity) {
          return bSeverity - aSeverity; // Higher severity first
        }
        return (b.confidence || 0) - (a.confidence || 0); // Higher confidence first
      })
      .slice(0, 3)
      .map((flaw: any) => ({
        flaw_id: flaw.flaw_id,
        title: flaw.display_name || flaw.flaw_id,
        severity: flaw.severity,
        confidence: flaw.confidence || 0
      }));

    // Count total drills from all flaws
    const drillsCount = flaws.reduce((total: number, flaw: any) => {
      return total + (flaw.drills?.length || 0);
    }, 0);

    return {
      analysis_id: data.id,
      timestamp: data.created_at,
      club: videoContext.club,
      swing_type: videoContext.swing_type,
      view: videoContext.view,
      primary_flaws: primaryFlaws,
      drills_count: drillsCount,
      video_asset_id: data.video_asset_id,
      status: data.status
    };
  }
}

export const runHistory = new RunHistoryService();